/**
 * OwnerAvatars — an overlapping avatar stack for an event's participants.
 *
 * Caps the number of visible avatars and renders a "+N" counter chip (styled
 * like an avatar) for the overflow, so a large participant list never blows out
 * the card. With a single owner it also shows the name; with `showName` off
 * (dense timeline) it shows avatars only. Every avatar (and the +N chip) has a
 * `title` so the full roster is discoverable on hover.
 */

import { cn } from '@/lib/cn';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import type { CalendarOwner } from './calendar.types';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

export interface OwnerAvatarsProps {
  owners: CalendarOwner[];
  /** Avatar diameter in px. */
  size?: number;
  /** Max avatars before collapsing into a "+N" chip. */
  max?: number;
  /** Show the single owner's name (lists) vs avatars-only (dense timeline). */
  showName?: boolean;
  className?: string;
}

export function OwnerAvatars({
  owners,
  size = 22,
  max = 4,
  showName = true,
  className,
}: OwnerAvatarsProps) {
  if (owners.length === 0) return null;

  // Reserve one slot for the +N chip when overflowing, so "max" is the total
  // number of circles shown (avatars + chip).
  const overflow = owners.length > max ? owners.length - (max - 1) : 0;
  const shown = overflow > 0 ? owners.slice(0, max - 1) : owners;
  const dim = { width: size, height: size };
  const fontPx = Math.max(9, Math.round(size * 0.42));
  const hiddenNames = overflow > 0 ? owners.slice(max - 1).map((o) => o.name).join(', ') : '';

  return (
    <div className={cn('flex min-w-0 items-center', className)}>
      <div className="flex -space-x-1">
        {shown.map((o, i) => (
          <Avatar key={`${o.name}-${i}`} className="ring-2 ring-card" style={dim} title={o.name}>
            {o.avatarUrl && <AvatarImage src={o.avatarUrl} alt={o.name} />}
            <AvatarFallback
              className="bg-muted font-medium text-muted-foreground"
              style={{ fontSize: fontPx }}
            >
              {initials(o.name)}
            </AvatarFallback>
          </Avatar>
        ))}
        {overflow > 0 && (
          <span
            className="flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground ring-2 ring-card"
            style={{ ...dim, fontSize: fontPx }}
            title={hiddenNames}
          >
            +{overflow}
          </span>
        )}
      </div>
      {showName && owners.length === 1 && (
        <span className="ml-2 truncate text-xs text-muted-foreground">{owners[0].name}</span>
      )}
    </div>
  );
}
